import {
  Account,
  AccountGroup,
  AdvertisingAccountType,
} from '@amzn/aac-chrome-internal-api';

/**
 * This function finds the global account or account to be highlighted in the account switcher.
 * The reason this is needed is because a global account can contain multiple accounts in different
 * countries.  We still want to high light the global account that the user is using.
 */
export const findSelectedAccountSwitcherAccount = (
  currentEntityId: string,
  accounts: Account[],
): Account | undefined => {
  for (const account of accounts) {
    // Case when currentEntityId matches global account
    if (account.entityId === currentEntityId) {
      return account;
    }

    // Case when currentEntityId matches an account under a global account.
    // We return the global account id in this case
    if (
      account.nestedAccounts &&
      Array.isArray(account.nestedAccounts) &&
      findSelectedAccountSwitcherAccount(
        currentEntityId,
        account.nestedAccounts,
      ) !== undefined
    ) {
      return account;
    }
  }

  return undefined;
};

export const getSingleGlobalAccount = (
  accounts: AccountGroup[],
  entityId: string,
): Account | undefined => {
  const singleGlobalAccountGroup = accounts?.find(
    (accountGroup) =>
      accountGroup.accountType === AdvertisingAccountType.GLOBAL,
  );
  if (!singleGlobalAccountGroup) {
    return undefined;
  }
  return findSelectedAccountSwitcherAccount(
    entityId,
    singleGlobalAccountGroup.accounts,
  );
};

export const getGlobalSponsoredAdsAccountFromAccountGroup = (
  currentEntityId: string,
  currentEntityType: AdvertisingAccountType,
  accounts: AccountGroup[],
): Account | undefined => {
  if (currentEntityType !== AdvertisingAccountType.SPONSORED_ADS) {
    return undefined;
  }

  const sponsoredAdsAccountGroup = accounts.find(
    (accountGroup) =>
      accountGroup.accountType === AdvertisingAccountType.SPONSORED_ADS,
  );

  // this case should never happen if the currentEntityType is SPONSORED_ADS
  if (!sponsoredAdsAccountGroup) {
    return undefined;
  }

  const topLevelAccount = findSelectedAccountSwitcherAccount(
    currentEntityId,
    sponsoredAdsAccountGroup.accounts,
  );

  // we determine if it is a global account if there is an account in the nestedAccounts prop
  return topLevelAccount &&
    Array.isArray(topLevelAccount.nestedAccounts) &&
    topLevelAccount.nestedAccounts.length > 0
    ? topLevelAccount
    : undefined;
};

export const updateMarketplaceLabelWithRetailerName = (
  marketplaceLabel: string,
  retailerName: string,
): string => {
  if (!retailerName) {
    return marketplaceLabel;
  }

  // Only Sponsored ads (Brand) accounts can have an associated retailerName.
  // Sponsored ads accounts always contain a marketplace name in the marketplaceLabel.
  // Therefore, we need to insert the retailer name between the account type name and marketplace name
  const marketplaceLabelArr = marketplaceLabel.split(', ');
  marketplaceLabelArr.splice(1, 0, retailerName);
  return marketplaceLabelArr.join(', ');
};
